/**
 * ValidationTable - Validation table component with multi-category filter
 * 
 * Story 1.5: Enhanced multi-category filter with:
 * - By Item Type (multi-select): Menu Items, Modifier Groups, Modifiers
 * - By Validation Type (multi-select): Image, Price, Description, Translation fields
 * 
 * Displays filterable table of validation results with validation field columns
 */

/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import ValidationRow from "./ValidationRow";
import { 
  VALIDATION_TYPES, 
  VALIDATION_TYPE_NAMES,
  VALIDATION_FIELD_TYPES,
  VALIDATION_FIELD_NAMES,
  VALIDATION_STATUS
} from "../../utils/enums";

const ValidationTable = ({ validationResults, menuId, baseLanguage, onOpenModal, onOpenPanel, isPanelOpen }) => {
  // Story 1.5: Multi-select filter state
  const [selectedItemTypes, setSelectedItemTypes] = useState([]);
  const [selectedValidationFields, setSelectedValidationFields] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterOpen]);

  if (!validationResults || validationResults.length === 0) {
    return (
      <div className="ls-validation-table-empty">
        <p>No validation results available.</p>
      </div>
    );
  }

  // Story 1.5: Multi-criteria filter logic
  const filteredResults = validationResults.filter((item) => {
    // Item type filter (AND logic - must match if any selected)
    const matchesItemType = selectedItemTypes.length === 0 || 
      selectedItemTypes.includes(item.type);
    
    // Validation field filter (OR logic within validation fields)
    const matchesValidationField = selectedValidationFields.length === 0 || 
      selectedValidationFields.some(fieldKey => {
        const validation = item.validation[fieldKey];
        return validation && (
          validation.status === VALIDATION_STATUS.MISSING ||
          validation.status === VALIDATION_STATUS.INVALID
        );
      });
    
    return matchesItemType && matchesValidationField;
  });

  // Handler for item type checkbox change
  const handleItemTypeChange = (type) => {
    setSelectedItemTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type])
  };

  // Handler for validation field checkbox change
  const handleValidationFieldChange = (fieldKey) => {
    setSelectedValidationFields(prev => prev.includes(fieldKey) ? prev.filter(f => f !== fieldKey) : [...prev, fieldKey])
  };

  // Get filter label with count
  const getFilterLabel = () => {
    const totalActive = selectedItemTypes.length + selectedValidationFields.length;
    if (totalActive === 0) return "Filter";
    return `Filter (${totalActive})`;
  };

  return (
    <div className="ls-validation-table-wrapper">
      <div className="ls-validation-table-header">
        <h2 className="ls-validation-table-title">Items with Missing Fields</h2>
        <div className="ls-validation-filter" ref={filterRef}>
          <button
            className="ls-validation-filter-btn"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            <svg className="ls-validation-filter-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.16675 5.83333H15.8334M6.66675 10H13.3334M8.75008 14.1667H11.2501" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{getFilterLabel()}</span>
          </button>
          {isFilterOpen && (
            <div className="ls-validation-filter-dropdown">
              {/* Category 1: By Item Type */}
              <div className="ls-validation-filter-category">
                <div className="ls-validation-filter-category-header">By Item Type</div>
                <label className="ls-validation-filter-checkbox">
                  <input 
                    type="checkbox" 
                    checked={selectedItemTypes.includes(VALIDATION_TYPES.ITEM)}
                    onChange={() => handleItemTypeChange(VALIDATION_TYPES.ITEM)}
                  />
                  <span>{VALIDATION_TYPE_NAMES.ITEM}s</span>
                </label>
                <label className="ls-validation-filter-checkbox">
                  <input 
                    type="checkbox" 
                    checked={selectedItemTypes.includes(VALIDATION_TYPES.MODIFIER)}
                    onChange={() => handleItemTypeChange(VALIDATION_TYPES.MODIFIER)}
                  />
                  <span>{VALIDATION_TYPE_NAMES.MODIFIER}s</span>
                </label>
                <label className="ls-validation-filter-checkbox">
                  <input 
                    type="checkbox" 
                    checked={selectedItemTypes.includes(VALIDATION_TYPES.MODIFIER_GROUP)}
                    onChange={() => handleItemTypeChange(VALIDATION_TYPES.MODIFIER_GROUP)}
                  />
                  <span>{VALIDATION_TYPE_NAMES.MODIFIER_GROUP}s</span>
                </label>
              </div>
              
              {/* Divider */}
              <div className="ls-validation-filter-divider" />
              
              {/* Category 2: By Validation Type */}
              <div className="ls-validation-filter-category">
                <div className="ls-validation-filter-category-header">By Validation Type</div>
                {Object.entries(VALIDATION_FIELD_TYPES).map(([key, value]) => (
                  <label key={key} className="ls-validation-filter-checkbox">
                    <input 
                      type="checkbox" 
                      checked={selectedValidationFields.includes(value)}
                      onChange={() => handleValidationFieldChange(value)}
                    />
                    <span>{VALIDATION_FIELD_NAMES[value]}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Story 1.7: Table container with independent vertical scrolling */}
      <div className="ls-validation-table-container">
        <table className="ls-validation-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Name</th>
              <th>Category</th>
              <th>Image</th>
              <th>Description</th>
              <th>Name Translation</th>
              <th>Description Translation</th>
              <th>Price</th>
              <th>{VALIDATION_FIELD_NAMES[VALIDATION_FIELD_TYPES.NUTRITION_INFO]}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.map((result) => (
              <ValidationRow
                key={result.id}
                validationResult={result}
                menuId={menuId}
                baseLanguage={baseLanguage}
                onOpenModal={onOpenModal}
                onOpenPanel={onOpenPanel}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ValidationTable;
